import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Spinner } from "@/components/ui/spinner";
import { formatDateTime } from "@/lib/utils";
import {
  AGREEMENT_TYPE_LABELS,
  type AgreementType,
} from "@/services/admin-agreement.service";
import type { TenantAgreementStatusDto } from "@/services/admin-tenant.service";

interface AgreementCommitmentsCardProps {
  statuses?: TenantAgreementStatusDto[];

  /** Whether the Revoke buttons are shown (SuperAdmin only). */
  canRevoke: boolean;

  /** What a revocation removes and who is asked again, as the confirm dialog says it. */
  description: string;

  /**
   * Label of the dialog's checkbox that extends the revocation to the other side. Without it the
   * dialog shows no checkbox and `includeRelated` is always false.
   */
  includeRelatedLabel?: string;

  isPending: boolean;

  /** Revokes one commitment; the dialog stays open until it settles. */
  onRevoke: (type: string, includeRelated: boolean) => Promise<unknown>;
}

/**
 * The commitments a tenant or a user has accepted, each with a Revoke button that asks for
 * confirmation.
 */
export function AgreementCommitmentsCard({
  statuses,
  canRevoke,
  description,
  includeRelatedLabel,
  isPending,
  onRevoke,
}: AgreementCommitmentsCardProps) {
  // Open and target are kept apart so the title keeps its commitment while the dialog animates
  // closed, instead of flashing an empty name.
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [revokeType, setRevokeType] = useState<string | null>(null);
  const [includeRelated, setIncludeRelated] = useState(false);

  const openRevoke = (type: string) => {
    setIncludeRelated(false);
    setRevokeType(type);
    setDialogOpen(true);
  };

  // The dialog stays open while the request and the refetch after it run, and closes once they
  // settle either way: the mutation's toast reports the outcome.
  const confirmRevoke = async () => {
    if (!revokeType) {
      return;
    }

    try {
      await onRevoke(revokeType, includeRelated);
    } catch {
      // Already reported by the mutation's error toast.
    } finally {
      setDialogOpen(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Commitments</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {(!statuses || statuses.length === 0) && (
          <p className="text-sm text-muted-foreground">
            No commitment has been published.
          </p>
        )}
        {statuses?.map((status) => (
          <div
            key={status.type}
            className="flex items-center justify-between gap-3 border-b pb-3 last:border-b-0 last:pb-0"
          >
            <div className="min-w-0 space-y-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">{labelOf(status.type)}</span>
                <AgreementStateBadge state={status.state} />
              </div>
              <p
                className="truncate text-xs text-muted-foreground"
                title={describeAcceptance(status)}
              >
                {describeAcceptance(status)}
              </p>
            </div>
            {canRevoke && status.acceptedVersion != null && (
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 text-destructive"
                disabled={isPending}
                onClick={() => openRevoke(status.type)}
              >
                Revoke
              </Button>
            )}
          </div>
        ))}
      </CardContent>

      <AlertDialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          if (!open && !isPending) {
            setDialogOpen(false);
          }
        }}
      >
        <AlertDialogContent
          onEscapeKeyDown={(event) => {
            if (isPending) {
              event.preventDefault();
            }
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>
              Revoke “{revokeType ? labelOf(revokeType) : ""}”?
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody className="space-y-4">
            <AlertDialogDescription>{description}</AlertDialogDescription>
            {includeRelatedLabel && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="revokeIncludeRelated"
                  checked={includeRelated}
                  disabled={isPending}
                  onCheckedChange={(checked) =>
                    setIncludeRelated(checked === true)
                  }
                />
                <Label
                  htmlFor="revokeIncludeRelated"
                  className="cursor-pointer"
                >
                  {includeRelatedLabel}
                </Label>
              </div>
            )}
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                // Radix closes on click; hold the dialog so the spinner covers the request.
                event.preventDefault();
                void confirmRevoke();
              }}
              disabled={isPending}
              className="bg-destructive"
            >
              {isPending && <Spinner className="mr-2" />}
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function labelOf(type: string): string {
  return AGREEMENT_TYPE_LABELS[type as AgreementType] ?? type;
}

/** The last acceptance in one line: version, time and who, or that there is none. */
function describeAcceptance(status: TenantAgreementStatusDto): string {
  if (!status.acceptedAt) {
    return "Never accepted";
  }

  const by = status.acceptedByEmail ? ` by ${status.acceptedByEmail}` : "";
  return `Last accepted v${status.acceptedVersion} on ${formatDateTime(status.acceptedAt)}${by}`;
}

/**
 * NotApplicable is its own state — nobody is being asked — and must never read as "not agreed".
 */
function AgreementStateBadge({
  state,
}: {
  state: TenantAgreementStatusDto["state"];
}) {
  if (state === "Accepted") {
    return (
      <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">
        Accepted
      </Badge>
    );
  }

  if (state === "Outstanding") {
    return (
      <Badge
        variant="outline"
        className="border-amber-500/50 text-amber-700 dark:text-amber-400"
      >
        Outstanding
      </Badge>
    );
  }

  return <Badge variant="secondary">Not asked</Badge>;
}
